import { useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconPortal } from '@components/icons/portal';
import { MaxLengthCounter } from '@components/max-length-counter';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { PortalEditErrors, useUpdatePortal } from 'api/portals/hooks';
import { PortalDetail } from 'api/portals/requests';
export const usePortalDetailsSidebar = () =>
  useSidebar(PortalDetailsSidebar, {
    variant: 'REGULAR',
  });

type PortalFormData = {
  name: string;
  description: string;
};

const Form = createForm<PortalFormData>();

export const PortalDetailsSidebar = ({
  portal,
  isEditing = false,
}: {
  portal: PortalDetail;
  isEditing?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const hasErrors = formStore.useFormHasErrors();
  const [errors, setErrors] = useState<PortalEditErrors>();
  const updatePortalMutation = useUpdatePortal(
    portal.shortId,
    {
      onSuccess: hideSidebar,
    },
    setErrors,
  );
  const isDirty = formStore.useIsDirty();
  const showEditSidebar = usePortalDetailsSidebar();

  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);

  const handleSubmit = async (formData: PortalFormData) => {
    await updatePortalMutation.mutateAsync({
      name: formData.name,
      description: formData.description,
    });
  };

  const description = (formStore.useFieldValue('description') as string) ?? '';

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('portalDetails')} icon={IconPortal} iconColor="elementBlue100">
        {!isEditing && (
          <Button
            text={t('core.edit')}
            variant="ghost"
            onClick={() => showEditSidebar({ portal, isEditing: true })}
          />
        )}
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        {isEditing ? (
          <Form store={formStore} onSubmit={handleSubmit} updateErrors={errors}>
            <Form.TextField
              id="name"
              label={t('title')}
              maxLength={50}
              defaultValue={portal.name}
            />
            <Stack space={1}>
              <Form.Textarea
                id="description"
                label={t('description')}
                defaultValue={portal.description ?? ''}
              />
              <MaxLengthCounter
                currentLength={description.length}
                maxLength={255}
                onExceedStatusChange={setIsCharacterLimitExceeded}
              />
            </Stack>
            <Sidebar.Footer>
              <Form.Actions>
                <Form.Reset
                  text={t('core.cancel')}
                  variant="secondary"
                  onClick={hideSidebar}
                  isDisabled={updatePortalMutation.isLoading}
                />
                <Form.Submit
                  text={t('core.save')}
                  variant="primary-blue"
                  isDisabled={
                    updatePortalMutation.isLoading ||
                    !isDirty ||
                    isCharacterLimitExceeded ||
                    hasErrors
                  }
                />
              </Form.Actions>
            </Sidebar.Footer>
          </Form>
        ) : (
          <>
            <Stack space={4}>
              <Text text={portal.name} font="heading2" />
              {portal.description ? (
                <Text text={portal.description} font="regular14" />
              ) : (
                <Text text={t('noDescriptionAdded')} font="regular14" color="textHelper" />
              )}
            </Stack>
          </>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

export const locale = {
  en: {
    portalDetails: 'Portal details',
    title: 'Title',
    description: 'Description',
    noDescriptionAdded: 'No description added',
  },
};
